import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Upload,
  Inbox,
  BookOpen,
  Network,
  FileText,
  Settings,
  Sparkles,
  LogOut,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { analystApi, getInboxCounts } from '../../services/api';

interface NavItem {
  to:    string;
  label: string;
  icon:  React.ReactNode;
  badge?: number;
}

export function Sidebar() {
  const { logout } = useAuth();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);
  const [analystWaiting, setAnalystWaiting] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const fetchCounts = () => {
      getInboxCounts()
        .then((c) => { if (!cancelled) setPendingCount(c.pending); })
        .catch(() => {});
    };

    const fetchAnalyst = () => {
      analystApi.summary()
        .then((s) => { if (!cancelled) setAnalystWaiting(s.waitingInput); })
        .catch((err: unknown) => console.warn('[sidebar] riepilogo dell\'analista non disponibile', err));
    };

    fetchCounts();
    fetchAnalyst();
    const interval = setInterval(() => { fetchCounts(); fetchAnalyst(); }, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const navItems: NavItem[] = [
    { to: '/dashboard', label: 'Dashboard',      icon: <LayoutDashboard className="w-5 h-5" /> },
    { to: '/ingest',    label: 'Acquisizione',   icon: <Upload className="w-5 h-5" /> },
    { to: '/inbox',     label: 'Inbox',          icon: <Inbox className="w-5 h-5" />, badge: pendingCount },
    { to: '/library',   label: 'Knowledge Base', icon: <BookOpen className="w-5 h-5" /> },
    { to: '/graph',     label: 'Grafo',          icon: <Network className="w-5 h-5" /> },
    { to: '/analyst',   label: 'Analista',       icon: <Sparkles className="w-5 h-5" />, badge: analystWaiting },
    { to: '/report',    label: 'Articoli',       icon: <FileText className="w-5 h-5" /> },
    { to: '/settings',  label: 'Impostazioni',   icon: <Settings className="w-5 h-5" /> },
  ];

  return (
    <aside className="w-56 shrink-0 bg-gray-900 border-r border-gray-800 flex flex-col h-full">
      {/* Logo */}
      <div className="px-5 py-4 border-b border-gray-800">
        <span className="text-white font-bold text-lg tracking-tight">AI Journalist</span>
        <span className="block text-gray-500 text-xs mt-0.5">V2.0</span>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {navItems.map((item) => {
          const isActive = location.pathname === item.to ||
            (item.to !== '/dashboard' && location.pathname.startsWith(item.to));
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              {item.icon}
              <span className="flex-1">{item.label}</span>
              {item.badge != null && item.badge > 0 && (
                <span className="bg-orange-500 text-white text-xs font-bold rounded-full min-w-[1.25rem] h-5 flex items-center justify-center px-1">
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-2 border-t border-gray-800">
        <button
          onClick={logout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span>Esci</span>
        </button>
      </div>
    </aside>
  );
}
